import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { Formik } from 'formik';

import { Column, FormInput, useToast } from 'Shared/components';
import { useLazyQuoteApi } from 'Shared/services/Api';
import { isPresent, stringToBoolean } from 'Shared/utils';

import {
  GetDetailsResponseType,
  transformAppDetails,
} from 'QuoteApp/services/endpoints/GetDetails';
import {
  DriverLicenseChallengeFieldsType,
  DriverLicenseChallengeFormValuesType,
  transformDriverLicenseRequest,
} from 'QuoteApp/services/endpoints/PostChallengeDriverLicense';
import { useAppContext } from 'QuoteApp/services/providers';
import {
  generateFieldDataTestId,
  getDriverDataByFieldValue,
  getErrorMessage,
} from 'QuoteApp/utils';
import { useEdit } from 'QuoteApp/utils/contexts';
import {
  getReportMessages,
  ReportChangeMessageType,
  ReportMessages,
} from 'QuoteApp/screens/Reports';
import { ChallengeForm } from '../../ChallengeForm';
import { ReportDetailHeaderRow } from '../../ReportDetailHeaderRow';
import { ReportDetailRow, getDisplayValue } from '../../ReportDetailRow';
import styles from './styles.module.css';
import { DriverReportType } from '../../types';
import LicenseVerified from './LicenseVerified';

export const DriverLicenseReport: DriverReportType = ({ driverId }) => {
  const { details, config, appId, setDetails, fetchRate, rate } =
    useAppContext();
  const { editingId, changeEditingId } = useEdit();
  const { toast } = useToast();
  const identifier = `${driverId}-driverLicense`;
  const [messages, setMessages] = useState<Array<ReportChangeMessageType>>([]);

  const [, challengeDriverLicenseData] =
    useLazyQuoteApi<GetDetailsResponseType>({
      method: 'POST',
      url: `/insurance_applications/${appId}/insurable_parties/${driverId}/challenge_driver_license`,
    });

  const driverData = useMemo(
    () => getDriverDataByFieldValue(details.drivers, 'value')[driverId],
    [details, driverId]
  );

  const challengeCommand = details.commands.drivers[
    driverId
  ].challenging.driverLicense.find(
    (c) => c.name === 'challenge_driver_license'
  );

  if (!config) return null;

  const { askForUserReportedStatus } = config.drivers.driverLicense;

  return (
    <Formik<DriverLicenseChallengeFormValuesType>
      enableReinitialize
      initialValues={{
        [identifier]: {
          licenseState: driverData.licenseState,
          licenseNumber: driverData.licenseNumber,
          licenseStatus: driverData.licenseStatus,
          licenseReportStatus: driverData.licenseReportStatus,
          licenseVerified: askForUserReportedStatus
            ? 'true'
            : driverData.licenseVerified,
          reasonText: '',
        },
      }}
      onSubmit={async (values) => {
        try {
          const response = await challengeDriverLicenseData({
            data: values[identifier],
            transformRequest: transformDriverLicenseRequest,
            transformResponse: transformAppDetails,
          });
          if (response.data.status === 'open') {
            const newRate = await fetchRate();
            rate &&
              newRate &&
              setMessages(
                getReportMessages(
                  rate,
                  newRate,
                  'disputed report data',
                  'drivers-license-challenging'
                )
              );
          }
          changeEditingId(null);
          setDetails(response.data);
        } catch (e) {
          toast({
            type: 'error',
            message: getErrorMessage(e),
            withSecondaryNav: false,
            icon: 'error',
          });
        }
      }}
    >
      {({ values, setFieldValue, handleBlur }) => {
        const disableSubmit =
          editingId === identifier &&
          !stringToBoolean(values[identifier].licenseVerified);

        const getInputProps = (
          fieldName: keyof DriverLicenseChallengeFieldsType
        ) => {
          const fullFieldName = `${identifier}.${fieldName}`;
          return {
            name: fullFieldName,
            className: styles.formInput,
            'data-testid': generateFieldDataTestId(fieldName),
            variant: 'selectAutocomplete' as const,
            onChange: (value: string) => {
              setFieldValue(fullFieldName, value);
            },
            onBlur: handleBlur,
            value: values[identifier][fieldName],
          };
        };

        const displayStatusValue = (
          values: DriverLicenseChallengeFieldsType
        ) => {
          switch (true) {
            case !askForUserReportedStatus:
              return config.drivers.driverLicense.status[values.licenseStatus];
            case !!values.licenseReportStatus:
              return config.drivers.driverLicense.reportStatus[
                values.licenseReportStatus
              ];
            case !!driverData.userReportedStatus:
              return config.drivers.driverLicense.userReportedStatus?.[
                driverData.userReportedStatus
              ];
            default:
              return null;
          }
        };

        return (
          <>
            <ReportDetailHeaderRow canChallenge={isPresent(challengeCommand)}>
              <Column variant="quarter">License State</Column>
              <Column variant="quarter">License Number</Column>
              <Column variant="quarter">License Status</Column>
              <Column variant="half">Data Verified</Column>
            </ReportDetailHeaderRow>
            <ul>
              <ReportDetailRow
                identifier={identifier}
                canChallenge={isPresent(challengeCommand)}
                challengeCommand={challengeCommand}
                source={details.drivers[driverId].licenseStatus.source}
                challengeForm={
                  <ChallengeForm
                    variant="edit"
                    identifier={identifier}
                    type="drivers-licence"
                    disableSubmit={disableSubmit}
                  />
                }
                type="driver-license"
              >
                <Column variant="quarter" className="fs-mask">
                  {getDisplayValue(
                    config.drivers.driverLicense.state[
                      values[identifier].licenseState
                    ]
                  )}
                </Column>
                <Column variant="quarter" className="fs-mask">
                  {getDisplayValue(values[identifier].licenseNumber)}
                </Column>
                <Column
                  variant="quarter"
                  className={classNames('fs-mask', styles.extendOptionWidth, {
                    [styles.editing]: editingId === identifier,
                  })}
                >
                  {editingId === identifier ? (
                    askForUserReportedStatus ? (
                      <FormInput
                        {...getInputProps('licenseReportStatus')}
                        label="License Status"
                        options={config.drivers.driverLicense.reportStatus}
                      />
                    ) : (
                      <FormInput
                        {...getInputProps('licenseStatus')}
                        label="License Status"
                        options={config.drivers.driverLicense.status}
                      />
                    )
                  ) : (
                    getDisplayValue(displayStatusValue(values[identifier]))
                  )}
                </Column>
                <Column
                  variant="half"
                  className={classNames('fs-mask', {
                    [styles.editing]: true,
                  })}
                >
                  <LicenseVerified
                    isEditing={editingId === identifier}
                    driverLicenseVerified={driverData.licenseVerified}
                    formLicenseVerified={values[identifier].licenseVerified}
                    onChange={getInputProps('licenseVerified').onChange}
                  />
                </Column>
              </ReportDetailRow>
            </ul>

            <ReportMessages messages={messages} />
          </>
        );
      }}
    </Formik>
  );
};
